import { PerspectiveCamera, Scene, Vector2, AxesHelper, WebGLRenderer, Clock, PCFSoftShadowMap, Group, Vector3, Color } from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { SMAAPass } from 'three/examples/jsm/postprocessing/SMAAPass.js';
import { BokehPass } from 'three/examples/jsm/postprocessing/BokehPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { FilmPass } from 'three/examples/jsm/postprocessing/FilmPass.js';

import { isDebug, isSmartphone, isTouch } from '../core/Basics';
import { GetBy } from '../core/Element';
import { Metrics } from '../core/Metrics';
import { Sizes } from '../core/Sizes';
import { CENTER, SETTINGS } from '../../../3D/constants';
import { Interaction } from '../core/Interaction';
import { Maths } from '../utils/Maths';
import BG from '../layout/Background';

export default class WebGLSketch {
    _started = false;
    _paused = false;

    static renderer;
    static domElement;
    static scene;
    static camera;
    static clock;
    static size;
    static controls;

    static group = new Group();
    static mouse = new Vector2();
    static cameraGroup = new Group();
    static cameraPosition = CENTER;
    static cameraLookAt = CENTER;
    static cameraLookAtTarget = CENTER;

    // Postprocesado DOF, Bloom, Film Grain y SMAA
    static composer;
    static renderPass;
    static bokehPass;
    static bloomPass;
    static filmPass;
    static smaaPass;
    static useDOF = true;
    static useBloom = false;
    static useFilmGrain = true;
    static transparentBackground = false;

	static grainFrameInterval = 3; // Actualizar cada 2 frames (30fps si render a 60fps)
    static grainFrameCounter = 0;
    static grainTimeOffset = 0;
    static grainUpdateRate = 1.0;
	static grainManualTime = 0; // ← Añadir esta línea que faltaba

    static defaults = {
        container: 'scene-three',
        antialias: true,
        alpha: false,
        ortho: false,
        camera2D: SETTINGS.camera2D,
        shadows: false,
        debug: isDebug,
        transparent: true,
        clearColorAlpha: 0,
        lookAt: SETTINGS.lookAt,
        cameraPos: SETTINGS.cameraPos,
        fov: SETTINGS.fov,
        controls: SETTINGS.controls,
        far: SETTINGS.far,
        near: SETTINGS.near,
        clearColor: SETTINGS.clearColor,
        clearColorAlpha: SETTINGS.clearColorAlpha,
    }

    static init(opts = {}) {
        this.defaults = {
            ...this.defaults,
            ...opts
        };

        this.domElement = GetBy.id(this.defaults.container);
        const { width, height } = this.domElement.getBoundingClientRect();
        this.size = this.defaults.size ? this.defaults.size : new Vector2(width, height);
        this.scene = new Scene();

        this.renderer = new WebGLRenderer({
            canvas: this.domElement,
            antialias: this.defaults.antialias,
            alpha: this.defaults.alpha,
            logarithmicDepthBuffer: false 
        });

        this.renderer.setClearColor(this.defaults.clearColor, this.defaults.clearColorAlpha);
        this.renderer.setSize(this.size.x, this.size.y);
        this.renderer.setPixelRatio(Sizes.RATIO_CANVAS);

        if (this.defaults.outputEncoding) {
            this.renderer.outputEncoding = this.defaults.outputEncoding;
        }

        if (this.defaults.shadows) {
            this.renderer.shadowMap.enabled = true;
            this.renderer.shadowMap.type = PCFSoftShadowMap;
        }

        this.camera = new PerspectiveCamera(
            this.defaults.fov,
            this.size.x / this.size.y,
            this.defaults.near,
            this.defaults.far
        );

        this.camera.position.copy(CENTER);
        this.cameraPosition.copy(this.defaults.cameraPos);
        this.cameraGroup.position.copy(this.defaults.cameraPos);

        this.camera.lookAt(this.defaults.lookAt);
        this.cameraLookAt.copy(this.defaults.lookAt);
        this.cameraLookAtTarget.copy(this.defaults.lookAt);

        if (this.defaults.camera2D) {
            this.camera.fov = 2 * Math.atan(this.size.x / this.camera.aspect / (2 * this.defaults.cameraPos.z)) * (180 / Math.PI);
        }

        this.camera.updateProjectionMatrix();

        this.cameraGroup.add(this.camera);
        this.scene.add(this.cameraGroup);

        if (this.defaults.debug) {
            const axesHelper = new AxesHelper(2000);
            this.scene.add(axesHelper);
        }

        this.clock = new Clock();
        this.scene.add(this.group);

        // Inicializar post-procesado si está activado
        this.initPostProcessing();
    }

    static initPostProcessing() {
        if (this.useDOF || this.useBloom || this.useFilmGrain) {
            this.composer = new EffectComposer(this.renderer);

            // Render Pass
            this.renderPass = new RenderPass(this.scene, this.camera);
            this.composer.addPass(this.renderPass);

            // Bloom Pass
            if (this.useBloom) {
                this.bloomPass = new UnrealBloomPass(
                    new Vector2(this.size.x, this.size.y),
                    1.5, 0.4, 0.85
                );
                
                this.bloomPass.threshold = 0.1;
                this.bloomPass.strength = 1.0;
                this.bloomPass.radius = 0.5;
                
                this.composer.addPass(this.bloomPass);
            }

            // Bokeh Pass
            if (this.useDOF) {
                this.bokehPass = new BokehPass(this.scene, this.camera, {
                    focus: 500.0,
                    aperture: 0.000021,
                    maxblur: 0.0121,    
                    width: this.size.x,
                    height: this.size.y
                });
                this.bokehPass.needsSwap = true;
                this.composer.addPass(this.bokehPass); 
                
                const color = BG.getColor()?.hex || this.defaults.clearColor;
                console.log(color)
                this.renderer.setClearColor(new Color(Number(color)), 1);
            } else {
                this.renderer.setClearColor(this.defaults.clearColor, 0);
            }

            // Film Grain Pass - CORREGIDO
			
            if (this.useFilmGrain) {
                this.filmPass = new FilmPass(0.2, 0.125, 256, true);
                
                // Inicializar grainManualTime si no existe
                if (this.grainManualTime === undefined) {
                    this.grainManualTime = 0;
                }
                
                // Sobrescribir el método render del FilmPass
                this.filmPass.render = (renderer, writeBuffer, readBuffer, deltaTime, maskActive) => {
                    // Configurar uniforms
                    this.filmPass.uniforms['tDiffuse'].value = readBuffer.texture;
                    this.filmPass.uniforms['time'].value = this.grainManualTime || 0;
                    
                    // Renderizar
                    if (this.filmPass.renderToScreen) {
                        renderer.setRenderTarget(null);
                        this.filmPass.fsQuad.render(renderer);
                    } else {
                        renderer.setRenderTarget(writeBuffer);
                        if (this.filmPass.clear) renderer.clear();
                        this.filmPass.fsQuad.render(renderer);
                    }
                };
                
                this.composer.addPass(this.filmPass);
            }

            // SMAA Pass
            this.smaaPass = new SMAAPass(this.size.x, this.size.y);
            this.composer.addPass(this.smaaPass);

            if (this.transparentBackground) {
                this.setTransparentBackground(true);
            }
        }
    }

    static time() {
        return this.clock.getElapsedTime();
    }

    static start() {
        if (this._started && !this._paused) return;

        if (!this._started) this.addEventListeners();

        this._paused = false;
        this._started = true;
        this.clock.start();

        if (this.defaults.controls) this.domElement.style.pointerEvents = 'initial';
        else this.domElement.style.pointerEvents = 'none';
    }

    static stop() {
        if (!this._started) return;
        if (this._paused) return;
        this._paused = true;
        this.clock.stop();
    }

    static addEventListeners() { }
    static removeEventListeners() { }

    static update() {
        if (this.defaults.controls) {
            this.cameraGroup.position.copy(SETTINGS.cameraPos);
            this.controls.update();
            return;
        }

        if (!isTouch && (SETTINGS.groupRotation || SETTINGS.cameraRotation)) {
            const center = {
                x: Interaction.positions.mouse.x - Metrics.CENTER_X,
                y: Interaction.positions.mouse.y - Metrics.CENTER_Y
            };

            const mx = Maths.map(center.x, - Metrics.CENTER_X, Metrics.CENTER_X, -SETTINGS.mouseX, SETTINGS.mouseX);
            const my = - Maths.map(center.y, - Metrics.CENTER_Y, Metrics.CENTER_Y, -SETTINGS.mouseY, SETTINGS.mouseY);
            this.mouse.x = Maths.lerp(this.mouse.x, mx, SETTINGS.mouseLerp);
            this.mouse.y = Maths.lerp(this.mouse.y, my, SETTINGS.mouseLerp);

            const rotationY = Maths.toRadians(this.mouse.x);
            const rotationX = - Maths.toRadians(this.mouse.y);

            if (SETTINGS.groupRotation) {
                this.group.rotation.x = rotationX;
                this.group.rotation.y = rotationY;
            }

            if (SETTINGS.cameraRotation) {
                this.cameraPosition.x += rotationX;
                this.cameraPosition.y += rotationY;
            }
        }

        if (!this.defaults.ortho) {
            this.cameraGroup.position.lerp(this.cameraPosition, SETTINGS.cameraLerp);
            this.cameraLookAt.lerp(this.cameraLookAtTarget, SETTINGS.cameraLerp);
            this.camera.lookAt(this.cameraLookAt);
        }

        // Actualizar foco DOF si está activo
        if (this.useDOF && this.bokehPass) {
            // Calcular distancia desde cámara a un objeto específico
            const targetPosition = new Vector3(0, 0, 0);
            const cameraWorldPosition = new Vector3();
            this.camera.getWorldPosition(cameraWorldPosition);
            
            const focusDistance = cameraWorldPosition.distanceTo(targetPosition);
            this.bokehPass.uniforms['focus'].value = focusDistance;
        }

		this.updateGrainAnimation();
    }

	static updateGrainAnimation() {
        if (!this.useFilmGrain || !this.filmPass) return;

        this.grainFrameCounter++;

        // Solo actualizar el grano cada X frames
        if (this.grainFrameCounter >= this.grainFrameInterval) {
            this.grainFrameCounter = 0;
            
            // Actualizar SOLO cuando queremos - usar grainManualTime en lugar de grainTimeOffset
            this.grainManualTime += 0.016 * this.grainUpdateRate;
        }
    }

    static render() {
        if ((this.useDOF || this.useBloom || this.useFilmGrain) && this.composer) {
            // Si hay transparencia, limpiar manualmente antes de renderizar
            if (this.transparentBackground) {
                this.renderer.clear();
            }
            this.composer.render();
        } else {
            this.renderer.render(this.scene, this.camera);
        }
    }

    static loop() {
        if (!this._started || this._paused) return;
        this.update();
        this.render();
    }

    static resize() {
        if (isSmartphone) return;

        this.domElement.style = '';
        const { width, height } = this.domElement.getBoundingClientRect();

        if (width === this.size.x && height === this.size.y) return;

        this.size.set(width, height);
        this.renderer.setSize(this.size.x, this.size.y);

        this.camera.aspect = this.size.x / this.size.y;
        this.cameraPosition.z = this.defaults.cameraPos.z;
        this.cameraGroup.position.z = this.defaults.cameraPos.z;

        if (this.defaults.camera2D) {
            this.camera.fov = 2 * Math.atan(this.size.x / this.camera.aspect / (2 * this.defaults.cameraPos.z)) * (180 / Math.PI);
        }

        this.camera.updateProjectionMatrix();

        // Actualizar composer y passes si están activos
        if ((this.useDOF || this.useBloom || this.useFilmGrain) && this.composer) {
            this.composer.setSize(this.size.x, this.size.y);
            
            // Actualizar tamaños de passes individuales
            if (this.bokehPass) {
                this.bokehPass.setSize(this.size.x, this.size.y);
            }
            if (this.bloomPass) {
                this.bloomPass.setSize(this.size.x, this.size.y);
            }
            if (this.smaaPass) {
                this.smaaPass.setSize(this.size.x, this.size.y);
            }
        }
    }

    static dispose() {
        this.removeEventListeners();
    }

	// ================== FUNCIONES DE CONTROL FRAME RATE GRANO ==================

    /**
     * Configura cada cuántos frames se actualiza el grano
     * @param {number} interval - Cada cuántos frames actualizar (1 = cada frame, 2 = cada 2 frames, etc.)
     */
   

    /**
     * Configura la velocidad de actualización del grano
     * @param {number} rate - Multiplicador de velocidad (1.0 = normal, 0.5 = mitad, 2.0 = doble)
     */
    static setGrainUpdateRate(rate) {
        this.grainUpdateRate = Math.max(0.1, rate);
    }

    /**
     * Configura el frame rate efectivo del grano
     * @param {number} targetFps - FPS deseado para el grano (ej: 15, 30, 60)
     */
    

    /**
     * Pausa completamente la animación del grano
     * @param {boolean} paused - true para pausar, false para continuar
     */
    static pauseGrainAnimation(paused = true) {
        if (paused) {
            this.grainUpdateRate = 0;
        } else {
            this.grainUpdateRate = 1.0;
        }
    }

    /**
     * Resetea el tiempo del grano
     */
    static resetGrainTime() {
        this.grainTimeOffset = 0;
        this.grainFrameCounter = 0;
        this.grainManualTime = 0; // ← Añadir esta línea
    }

    static setGrainFrameInterval(interval) {
        this.grainFrameInterval = Math.max(1, Math.floor(interval));
        this.grainFrameCounter = 0; // Reset counter para aplicar inmediatamente
    }

    static setGrainFPS(targetFps) {
        const renderFps = 60;
        this.grainFrameInterval = Math.max(1, Math.floor(renderFps / targetFps));
        this.grainFrameCounter = 0; // Reset counter
    }

    // Función de debug para verificar que funciona:
    static getGrainStatus() {
        return {
            frameInterval: this.grainFrameInterval,
            frameCounter: this.grainFrameCounter,
            manualTime: this.grainManualTime,
            updateRate: this.grainUpdateRate,
            isEnabled: this.useFilmGrain
        };
    }

    // ================== FUNCIONES DE CONTROL DOF ==================

    /**
     * Activa o desactiva el efecto de profundidad de campo (DOF)
     * @param {boolean} enable - true para activar DOF, false para desactivar
     */
    static enableDOF(enable = true) {
        this.useDOF = enable;
        this.initPostProcessing(); // Reinicializar passes
    }

    /**
     * Configura el foco del efecto DOF
     * @param {number} distance - Distancia de enfoque desde la cámara
     */
    static setDOFFocus(distance) {
        if (this.useDOF && this.bokehPass) {
            this.bokehPass.uniforms['focus'].value = distance;
        }
    }

    /**
     * Configura la apertura del efecto DOF (controla la intensidad del blur)
     * @param {number} aperture - Valor de apertura (valores más bajos = más blur)
     */
    static setDOFAperture(aperture) {
        if (this.useDOF && this.bokehPass) {
            this.bokehPass.uniforms['aperture'].value = aperture;
        }
    }

    /**
     * Configura el blur máximo del efecto DOF
     * @param {number} maxblur - Valor máximo de blur
     */
    static setDOFMaxBlur(maxblur) {
        if (this.useDOF && this.bokehPass) {
            this.bokehPass.uniforms['maxblur'].value = maxblur;
        }
    }

    /**
     * Configura todos los parámetros del DOF de una vez
     * @param {Object} params - {focus, aperture, maxblur}
     */
    static setDOFParams({focus, aperture, maxblur}) {
        if (this.useDOF && this.bokehPass) {
            if (focus !== undefined) this.bokehPass.uniforms['focus'].value = focus;
            if (aperture !== undefined) this.bokehPass.uniforms['aperture'].value = aperture;
            if (maxblur !== undefined) this.bokehPass.uniforms['maxblur'].value = maxblur;
        }
    }

    /**
     * Enfoca automáticamente un objeto específico
     * @param {Object3D} targetObject - El objeto Three.js a enfocar
     */
    static focusOnObject(targetObject) {
        if (this.useDOF && this.bokehPass && targetObject) {
            const cameraWorldPosition = new Vector3();
            const targetWorldPosition = new Vector3();
            
            this.camera.getWorldPosition(cameraWorldPosition);
            targetObject.getWorldPosition(targetWorldPosition);
            
            const focusDistance = cameraWorldPosition.distanceTo(targetWorldPosition);
            this.bokehPass.uniforms['focus'].value = focusDistance;
        }
    }

    // ================== FUNCIONES DE CONTROL BLOOM ==================

    /**
     * Activa o desactiva el efecto bloom
     * @param {boolean} enable - true para activar bloom, false para desactivar
     */
    static enableBloom(enable = true) {
        this.useBloom = enable;
        this.initPostProcessing(); // Reinicializar passes
    }

    /**
     * Configura la intensidad del bloom
     * @param {number} strength - Intensidad del bloom (0-5, defecto: 1.0)
     */
    static setBloomStrength(strength) {
        if (this.useBloom && this.bloomPass) {
            this.bloomPass.strength = strength;
        }
    }

    /**
     * Configura el umbral de brillo para el bloom
     * @param {number} threshold - Umbral de brillo (0-1, defecto: 0.1)
     */
    static setBloomThreshold(threshold) {
        if (this.useBloom && this.bloomPass) {
            this.bloomPass.threshold = threshold;
        }
    }

    /**
     * Configura el radio del efecto bloom
     * @param {number} radius - Radio del bloom (0-1, defecto: 0.5)
     */
    static setBloomRadius(radius) {
        if (this.useBloom && this.bloomPass) {
            this.bloomPass.radius = radius;
        }
    }

    /**
     * Configura todos los parámetros del bloom de una vez
     * @param {Object} params - {strength, threshold, radius}
     */
    static setBloomParams({strength, threshold, radius}) {
        if (this.useBloom && this.bloomPass) {
            if (strength !== undefined) this.bloomPass.strength = strength;
            if (threshold !== undefined) this.bloomPass.threshold = threshold;
            if (radius !== undefined) this.bloomPass.radius = radius;
        }
    }

    // ================== FUNCIONES DE CONTROL FILM GRAIN ==================

    /**
     * Activa o desactiva el efecto de grano de película
     * @param {boolean} enable - true para activar film grain, false para desactivar
     */
    static enableFilmGrain(enable = true) {
        this.useFilmGrain = enable;
        this.initPostProcessing(); // Reinicializar passes
    }

    /**
     * Configura la intensidad del ruido/grano
     * @param {number} intensity - Intensidad del ruido (0-1, defecto: 0.8)
     */
    static setGrainIntensity(intensity) {
        if (this.useFilmGrain && this.filmPass) {
            this.filmPass.uniforms['nIntensity'].value = intensity;
        }
    }

    /**
     * Configura la intensidad de las líneas de escaneo
     * @param {number} intensity - Intensidad de las líneas (0-1, defecto: 0.125)
     */
    static setScanlineIntensity(intensity) {
        if (this.useFilmGrain && this.filmPass) {
            this.filmPass.uniforms['sIntensity'].value = intensity;
        }
    }

    /**
     * Configura el número de líneas de escaneo
     * @param {number} count - Número de líneas (defecto: 256)
     */
    static setScanlineCount(count) {
        if (this.useFilmGrain && this.filmPass) {
            this.filmPass.uniforms['sCount'].value = count;
        }
    }

    /**
     * Activa o desactiva el modo escala de grises
     * @param {boolean} grayscale - true para B&N, false para color
     */
    static setGrainGrayscale(grayscale) {
        if (this.useFilmGrain && this.filmPass) {
            this.filmPass.uniforms['grayscale'].value = grayscale ? 1 : 0;
        }
    }

    /**
     * Configura todos los parámetros del grano de una vez
     * @param {Object} params - {intensity, scanlineIntensity, scanlineCount, grayscale}
     */
    static setFilmGrainParams({intensity, scanlineIntensity, scanlineCount, grayscale}) {
        if (this.useFilmGrain && this.filmPass) {
            if (intensity !== undefined) this.filmPass.uniforms['nIntensity'].value = intensity;
            if (scanlineIntensity !== undefined) this.filmPass.uniforms['sIntensity'].value = scanlineIntensity;
            if (scanlineCount !== undefined) this.filmPass.uniforms['sCount'].value = scanlineCount;
            if (grayscale !== undefined) this.filmPass.uniforms['grayscale'].value = grayscale ? 1 : 0;
        }
    }

    // ================== FUNCIONES DE CONTROL DE TRANSPARENCIA ==================

    /**
     * Activa o desactiva el fondo transparente
     * @param {boolean} transparent - true para fondo transparente, false para fondo normal
     */
    static setTransparentBackground(transparent = true) {
        this.transparentBackground = transparent;
        
        if ((this.useDOF || this.useBloom || this.useFilmGrain) && this.composer && this.renderPass) {
            if (transparent) {
                // Activar transparencia
                this.renderPass.clear = false;
                this.renderPass.clearAlpha = 0;
                this.renderPass.clearColor = new Color(0, 0, 0);
                
                // Configurar renderer para transparencia
                this.renderer.autoClear = false;
                this.renderer.setClearColor(this.defaults.clearColor, 0);
            } else {
                // Desactivar transparencia (fondo normal)
                this.renderPass.clear = true;
                this.renderPass.clearAlpha = this.defaults.clearColorAlpha;
                
                // Restaurar configuración del renderer
                this.renderer.autoClear = true;
                this.renderer.setClearColor(this.defaults.clearColor, this.defaults.clearColorAlpha);
            }
        }
    }

    /**
     * Alterna entre fondo transparente y fondo normal
     */
    static toggleTransparency() {
        this.setTransparentBackground(!this.transparentBackground);
    }

    /**
     * Obtiene el estado actual de la transparencia del fondo
     * @returns {boolean} true si el fondo es transparente, false si es normal
     */
    static isTransparentBackground() {
        return this.transparentBackground;
    }

    // ================== FUNCIONES DE CONTROL GENERAL ==================

    /**
     * Obtiene información del estado actual de todos los efectos
     * @returns {Object} Estado de todos los efectos
     */
    static getEffectsStatus() {
        return {
            dof: this.useDOF,
            bloom: this.useBloom,
            filmGrain: this.useFilmGrain,
            transparentBackground: this.transparentBackground
        };
    }

    /**
     * Configura múltiples efectos de una vez
     * @param {Object} effects - {dof, bloom, filmGrain}
     */
    static setEffects({dof, bloom, filmGrain}) {
        let needsReinit = false;
        
        if (dof !== undefined && dof !== this.useDOF) {
            this.useDOF = dof;
            needsReinit = true;
        }
        
        if (bloom !== undefined && bloom !== this.useBloom) {
            this.useBloom = bloom;
            needsReinit = true;
        }
        
        if (filmGrain !== undefined && filmGrain !== this.useFilmGrain) {
            this.useFilmGrain = filmGrain;
            needsReinit = true;
        }
        
        if (needsReinit) {
            this.initPostProcessing();
        }
    }

    /**
     * Desactiva todos los efectos de post-procesado
     */
    static disableAllEffects() {
        this.setEffects({
            dof: false,
            bloom: false,
            filmGrain: false
        });
    }

    /**
     * Activa todos los efectos de post-procesado
     */
    static enableAllEffects() {
        this.setEffects({
            dof: true,
            bloom: true,
            filmGrain: true
        });
    }
}
